import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import {
  Stack,
  PrimaryButton,
  ScrollablePane,
  ScrollbarVisibility,
} from "@fluentui/react";
import {
  DATA_LOAD_STATUS,
  CHECKIN_PROCESS_TYPE,
  INVALID_DOCUMENT,
  DOC_CHECK_IN_TYPE,
  PAGE_NAME,
  CREATE_VIEW_COPY_SUPPORT,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
  USER_TOKEN_STATUS,
  DOCUMENT_LOAD_STATUS,
  CHECKIN_NEW_REVISION,
} from "../../../redux/constants/metaData";
import { ON_VIEWREQUIRED_CHANGED, ON_FILEUPLOAD_CHANGE } from "../../../redux/constants/actionTypes";
import {
  getFileTypeMismatchError,
  SUCCESS_TYPE,
  CHECKIN_VALIDATION_DOCUMENT_REQUIRED,
  CHECKIN_VALIDATION_NEW_REVISION_REQUIRED,
  CHECKIN_VALIDATION_REVISION_TEXT_REQUIRED,
  CHECKIN_VALIDATION_VIEW_COPY_REQUIRED,
  CHECKIN_VALIDATION_NEW_REVISION_MAX_LENGTH,
} from "../../../redux/constants/messageTypes";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_ROUTES, NAV_LOCATION_STATE } from "../../../routes/routeConfig";
import { useTools } from "../../../redux/hooks/useTools";
import { useCheckinPanelStyle } from "../../../theme/styles/CheckinPanelStyle";
import NewRevisionInfoContent from "./NewRevisionInfoContent";
import RevisionDocumentFields from "./RevisionDocumentFields";
import FileTypeMismatchAlert from "./FileTypeMismatchAlert";
import RevisionValidationMessageBar from "./RevisionValidationMessageBar";
import ViewCopyAccordionSection from "./ViewCopyAccordionSection";
import ReleaseDocumentCheckbox from "./ReleaseDocumentCheckbox";
import { useTheme } from "../../../theme/OAS365Theme";
import { useCommonControlsStyle } from "../../../theme/styles/CommonControlsStyle";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { ON_ATTACHED_DOC_INFO } from "../../../redux/constants/actionTypes";
import {
  fetchOneDriveDownloadUrl as fetchOneDriveDownloadUrlThunk,
  clearOneDriveDownloadUrl,
} from "../../../redux/slices/oneDrive";
import { requestRefreshCheckInNewList } from "../../../redux/slices/oneDrive/oneDriveDocsListSlice";
import { useOneDrive } from "../../../redux/hooks/useOneDrive";
import { useWorkingDocument } from "../../../redux/hooks/useWorkingDocument";
import DeleteOneDriveCheckbox from "./DeleteOneDriveCheckbox";
import { useCheckin } from "../../../redux/hooks/useCheckin";
import { useMetadata } from "../../../redux/hooks/useMetadata";
import { useTeamsRequestContext } from "../../../redux/hooks/useTeamsRequestContext";
import { useLogin } from "../../../redux/hooks/useLogin";
import { resolveGroupId } from "../../check-in-new-list/checkInNewList.utils";
import UserAuth from "../../../auth/userAuth";
import AuthTeams from "../../../auth/AuthTeams";
import {
  sanitizeFileName,
  getDocumentRevision,
  getTitle,
  getFileExtension,
  parseIfsIdentifiersFromFileName,
  isWorkingDocumentDownloaded,
} from "../../../functions/helperMethod";
import {
  updateOneDriveMetadata,
  postCheckinDocument,
  approveAndReleaseRevision,
} from "../../../redux/api/documentsAPI";
import { useActivePage } from "../../../redux/hooks/useActivePage";
import { useNewRevisionInfo } from "../../../redux/hooks/useNewRevisionInfo";
import { useCheckinNewRevision } from "../../../redux/hooks/useCheckinNewRevision";
import logger from "../../../utils/logger";
import ReusableMessageDialog from "../../../components/popupModals/ReusableMessageDialog";

/**
 * Shape of the document passed from Check-in New list when user chooses "Check-in New Revision".
 * webLinkUrl is the selected document's webUrl (used for view copy / download).
 * @typedef {{
 *   key: string;
 *   name: string;
 *   title: string;
 *   documentMode: string;
 *   downloadlink: string|null;
 *   fileType: string;
 *   iconName: string;
 *   modified: string;
 *   modifiedby: string;
 *   weblink: string;
 *   webLinkUrl?: string;
 * }} DocumentFromListItem
 */

const CheckinNewRevision = () => {
  const dispatch = useAppDispatch();
  const checkin = useCheckin();
  const metadata = useMetadata();
  const { teamsContext } = useLogin();
  const { tenantId } = useTeamsRequestContext();
  const documentRevisionData = useAppSelector(
    (state) => state.DocumentsSlice?.documentRevisionInfo?.documentRevisionData ?? {},
  );

  const {
    isLoading: newRevisionInfoLoading,
    isError: newRevisionInfoError,
    message: newRevisionInfoMessage,
    selectedRevisionItem: selectedRevisionItemFromSlice,
    newRevision: sliceNewRevision,
    newRevisionText,
    isViewCopyRequired,
    isFormatRequired,
    newRevisionObjCon,
    fetchNewRevisionInfo,
    clearNewRevisionInfo,
    validationError: newRevisionValidationError,
    onValidationError,
  } = useNewRevisionInfo();

  const {
    submitCheckinNewRevision,
    isLoading: checkinSubmitLoading,
    isError: checkinSubmitError,
    message: checkinSubmitMessage,
    data: checkinSubmitData,
    resetCheckinNewRevision,
    closeCheckinDialog,
    isDialogOpen: checkinDialogOpen,
    navigateToAllDocuments,
    consumeNavigateToAllDocuments,
  } = useCheckinNewRevision();

  const { deleteOneDriveDocument } = useOneDrive();
  const { refreshWorkingDocuments } = useWorkingDocument();

  const {
    viewCopyFiles,
    uploadFiles,
    uploadViewCopyCount,
    docReleaseStatus,
  } = checkin;


  const { docCheckinType } = metadata;

  const activePage = useActivePage();
  const location = useLocation();
  const { onSelectedPageChange, onDocClassFormatChange, onSuccessMessageInvoke, onRequestRefreshAllDocuments } = useTools();

  const [selectedDocRevision, setSelectedDocRevision] = useState(null);

  /** @type {[DocumentFromListItem | null, React.Dispatch<React.SetStateAction<DocumentFromListItem | null>>]} */
  const [documentFromList, setDocumentFromList] = useState(null);
  const hasAppliedRevisionPrefillRef = React.useRef(false);
  const hasUserEditedRevisionTextRef = React.useRef(false);

  /** Fetched URL for the current list row only (Redux download URL is not keyed by document id). */
  const [fetchedDownloadUrl, setFetchedDownloadUrl] = useState("");
  const latestListDocKeyRef = React.useRef("");
  latestListDocKeyRef.current =
    documentFromList?.key != null ? String(documentFromList.key) : "";

  const [localNewRevision, setLocalNewRevision] = useState("");
  const [localNewRevisionText, setLocalNewRevisionText] = useState("");
  const [localCreateViewCopy, setLocalCreateViewCopy] = useState(false);
  const [localNewRevisionObjCon, setLocalNewRevisionObjCon] = useState([]);
  const [localReleaseChecked, setLocalReleaseChecked] = useState(false);
  const [localDeleteOneDrive, setLocalDeleteOneDrive] = useState(true);
  /** Local state for the first view copy file type (e.g. PDF); synced from viewCopyFiles, cleared on cancel. */
  const [localViewCopyFileType, setLocalViewCopyFileType] = useState("");
  const [suppressCurrentPrefill, setSuppressCurrentPrefill] = useState(false);
  const [documentFieldFocusToken, setDocumentFieldFocusToken] = useState(0);
  const latestPrefillSignatureRef = React.useRef("");


  const documentIsCheckedOut =
    location.state?.isSourceCheckedOut === true && !selectedDocRevision;

  const fallbackSelectedRevisionItem = useMemo(() => {
    if (suppressCurrentPrefill) return null;
    const selectedDocument = location.state?.selectedDocument ?? null;
    const fileName = selectedDocument?.name ?? "";

    const parsedRevision = fileName ? getDocumentRevision(fileName)?.[0] : null;
    if (parsedRevision?.class && parsedRevision?.docNo && parsedRevision?.docRev) {
      return {
        class: parsedRevision.class,
        docNo: parsedRevision.docNo,
        docRev: parsedRevision.docRev,
        sheetNo: parsedRevision.sheetNo ?? "",
        title: getTitle(fileName) || fileName,
        documentNo: `${parsedRevision.docNo} - ${parsedRevision.docRev}`,
        fileExtension: getFileExtension(fileName),
        format: "",
      };
    }

    
    const stateRevisionItem = location.state?.selectedRevisionItem ?? null;
    if (
      stateRevisionItem?.class &&
      stateRevisionItem?.docNo &&
      stateRevisionItem?.docRev
    ) {
      return stateRevisionItem;
    }

    return null;
  }, [
    location.state?.selectedDocument,
    location.state?.selectedRevisionItem,
    suppressCurrentPrefill,
  ]);

  const latestRevisionForDisplay = useMemo(() => {
    if (documentIsCheckedOut) return "";
    const normalize = (value) => {
      if (value === null || value === undefined) return null;
      const str = typeof value === "string" ? value.trim() : String(value).trim();
      return str ? str : null;
    };
    const pickLatestDocRev = (obj) =>
      normalize(obj?.LatestDocRev) ?? normalize(obj?.latestDocRev);
    const pickDocRev = (obj) => normalize(obj?.docRev) ?? normalize(obj?.DocRev);

    const fromLatestDocRev =
      pickLatestDocRev(selectedRevisionItemFromSlice) ??
      pickLatestDocRev(selectedDocRevision) ??
      pickLatestDocRev(documentRevisionData) ??
      pickLatestDocRev(selectedRevisionItemFromSlice?.documentRevision) ??
      null;
    if (fromLatestDocRev) return fromLatestDocRev;

    const fromDocRev =
      pickDocRev(selectedDocRevision) ??
      pickDocRev(selectedRevisionItemFromSlice) ??
      pickDocRev(documentRevisionData);
    if (fromDocRev) return fromDocRev;
    const documentNo =
      selectedRevisionItemFromSlice?.documentNo ?? selectedDocRevision?.documentNo ?? "";
    const parts = String(documentNo)
      .split("-")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    if (parts.length >= 2) return parts[parts.length - 1];
    return "";
  }, [
    selectedDocRevision,
    selectedRevisionItemFromSlice,
    documentRevisionData,
    documentIsCheckedOut,
  ]);

  const revisionItemForForm = useMemo(() => {
    // Checked-out source: keep the Document/Title fields empty (no auto-fill).
    if (documentIsCheckedOut) return null;
    if (!selectedDocRevision && !selectedRevisionItemFromSlice && !fallbackSelectedRevisionItem) {
      return null;
    }
    return {
      ...(fallbackSelectedRevisionItem ?? {}),
      ...(selectedDocRevision ?? {}),
      ...(selectedRevisionItemFromSlice ?? {}),
    };
  }, [selectedDocRevision, selectedRevisionItemFromSlice, fallbackSelectedRevisionItem, documentIsCheckedOut]);

  useLayoutEffect(() => {
    resetCheckinNewRevision();
    clearNewRevisionInfo();
    const fromCheckInNewAccordion =
      location.state?.from === APP_ROUTES.checkInNew;
    const hasRevisionPrefill = location.state?.selectedRevisionItem != null;
    if (fromCheckInNewAccordion && !hasRevisionPrefill) {
      hasAppliedRevisionPrefillRef.current = false;
      setSelectedDocRevision(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fromState = location.state?.selectedDocument ?? null;
    setDocumentFromList(fromState);
  }, [location.state?.selectedDocument]);

  useEffect(() => {
    const currentState = location.state ?? null;
    const selectedDocument = currentState?.selectedDocument ?? null;
    const selectedRevisionItem = currentState?.selectedRevisionItem ?? fallbackSelectedRevisionItem ?? null;
   
    if (!selectedDocument && !selectedRevisionItem) {
      return;
    }
    
    if (latestPrefillSignatureRef.current === currentState) return;

    latestPrefillSignatureRef.current = currentState;
    setSuppressCurrentPrefill(false);
    hasAppliedRevisionPrefillRef.current = false;
    setSelectedDocRevision(null);
  }, [location.state, fallbackSelectedRevisionItem]);

  useEffect(() => {
   
    if (location.state?.isSourceCheckedOut === true) return;
    const selectedRevisionItem =
      location.state?.selectedRevisionItem ?? fallbackSelectedRevisionItem ?? null;
    if (!selectedRevisionItem) return;
    if (hasAppliedRevisionPrefillRef.current) return;

    hasAppliedRevisionPrefillRef.current = true;

    setSelectedDocRevision(selectedRevisionItem);

    const hasCompleteIds =
      selectedRevisionItem.class &&
      selectedRevisionItem.docNo &&
      selectedRevisionItem.docRev;
    if (hasCompleteIds) {
      fetchNewRevisionInfo(selectedRevisionItem, {
        requestDocRevInfo: true,
        documentCheckInType: DOC_CHECK_IN_TYPE.checkinNewRevision,
        uploadFiles: uploadFiles ?? [],
        uploadViewCopyCount: uploadViewCopyCount ?? 0,
        isTemplate: location.state?.isTemplate === true,
      });
    }
  }, [
    location.state?.selectedRevisionItem,
    location.state?.isTemplate,
    location.state?.isSourceCheckedOut,
    fallbackSelectedRevisionItem,
    fetchNewRevisionInfo,
    uploadFiles,
    uploadViewCopyCount,
  ]);

  // Auto-load revision info when OneDrive/Channel document has stored IFS metadata
  useEffect(() => {
    if (location.state?.isSourceCheckedOut === true) return;
    if (location.state?.selectedRevisionItem) return;
    if (!documentFromList) return;
    if (hasAppliedRevisionPrefillRef.current) return;

    const doc = documentFromList;
    const hasIfsMetadata = doc.docClass && doc.docNo && doc.docRev;
    if (!hasIfsMetadata) return;

    hasAppliedRevisionPrefillRef.current = true;

    const fileExtension = doc.name?.includes(".") ? doc.name.split(".").pop() : (doc.fileExtension ?? "");
    const revisionItem = {
      class: doc.docClass,
      docNo: doc.docNo,
      docRev: doc.docRev,
      sheetNo: doc.docSheet || "0",
      title: doc.title || doc.name || "",
      documentNo: `${doc.docNo} - ${doc.docRev}`,
      fileExtension,
    };

    setSelectedDocRevision(revisionItem);
    fetchNewRevisionInfo(revisionItem, {
      requestDocRevInfo: true,
      documentCheckInType: DOC_CHECK_IN_TYPE.checkinNewRevision,
      uploadFiles: uploadFiles ?? [],
      uploadViewCopyCount: uploadViewCopyCount ?? 0,
      isTemplate: location.state?.isTemplate === true,
    });
  }, [
    documentFromList,
    location.state?.selectedRevisionItem,
    location.state?.isTemplate,
    location.state?.isSourceCheckedOut,
    fetchNewRevisionInfo,
    uploadFiles,
    uploadViewCopyCount,
  ]);

  useEffect(() => {
    setFetchedDownloadUrl("");
    dispatch(clearOneDriveDownloadUrl());
  }, [documentFromList?.key, dispatch]);

  
  useEffect(() => {
    if (documentFromList?.source === "browse") return;
    const hasDownloadUrl =
      location.state?.selectedDocumentDownloadUrl ||
      documentFromList?.downloadlink ||
      fetchedDownloadUrl;
    const documentId = documentFromList?.key != null ? String(documentFromList.key) : "";
    if (!documentId || hasDownloadUrl) return;

    const { groupId: teamIdStr, channelId: channelIdStr, fileRoot } = resolveGroupId(teamsContext);
    const oneDriveRootStr = String(fileRoot ?? "");
    const tenantIdStr = teamsContext?.user?.tenant?.id || tenantId || "";

    if (!tenantIdStr || !oneDriveRootStr) {
      logger.info("checkinNewRevision skip fetch: missing required params", {
        hasTenantId: !!tenantIdStr,
        hasOneDriveRoot: !!oneDriveRootStr,
        documentId: documentId ? `${documentId.substring(0, 8)}...` : "",
      });
      return;
    }

    const requestedId = documentId;
    let cancelled = false;

    logger.info("checkinNewRevision fetching download URL", {
      documentId: requestedId.substring(0, 12) + "...",
      tenantId: !!tenantIdStr,
      oneDriveRoot: oneDriveRootStr,
      teamIdEmpty: !teamIdStr,
      channelIdEmpty: !channelIdStr,
    });

    (async () => {
      try {
        const url = await dispatch(
          fetchOneDriveDownloadUrlThunk({
            teamId: teamIdStr || "",
            channelId: channelIdStr || "",
            tenantId: tenantIdStr,
            documentId: requestedId,
            fileRoot: oneDriveRootStr,
            operationType: DOC_CHECK_IN_TYPE.checkinNewRevision,
          })
        ).unwrap();
        if (cancelled) return;
        if (latestListDocKeyRef.current === requestedId && url) {
          setFetchedDownloadUrl(url);
        }
      } catch (err) {
        if (!cancelled) {
          logger.info("checkinNewRevision fetch download URL failed", {
            message: err?.message ?? String(err),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    documentFromList?.key,
    documentFromList?.source,
    documentFromList?.downloadlink,
    location.state?.selectedDocumentDownloadUrl,
    fetchedDownloadUrl,
    tenantId,
    teamsContext,
    dispatch,
  ]);




  useEffect(() => {
    if (revisionItemForForm == null) {
      setLocalNewRevision("");
      setLocalNewRevisionText("");
      setLocalCreateViewCopy(false);
      setLocalNewRevisionObjCon([]);
      hasUserEditedRevisionTextRef.current = false;
      return;
    }
    if (!newRevisionInfoLoading) {
      const resolvedRevision = sliceNewRevision ?? "";
      const resolvedRevisionText =
        (newRevisionText ?? "").trim() !== "" ? newRevisionText : resolvedRevision;
      setLocalNewRevision(sliceNewRevision ?? "");
      setLocalNewRevisionText(resolvedRevisionText);
      setLocalCreateViewCopy(isViewCopyRequired);
      setLocalNewRevisionObjCon(newRevisionObjCon);
      hasUserEditedRevisionTextRef.current = false;
    }
  }, [
    revisionItemForForm,
    sliceNewRevision,
    newRevisionText,
    isViewCopyRequired,
    newRevisionObjCon,
    newRevisionInfoLoading,
  ]);

  useEffect(() => {
    setLocalReleaseChecked(docReleaseStatus === "Released");
  }, [docReleaseStatus]);

  useEffect(() => {
    if (revisionItemForForm != null) {
      dispatch({
        type: ON_VIEWREQUIRED_CHANGED,
        payload: isViewCopyRequired ? "Y" : "N",
      });
    }
  }, [revisionItemForForm, isViewCopyRequired, dispatch]);

  useEffect(() => {
    const doc = documentFromList ?? selectedDocRevision;
    if (doc) {
      const extension = String(
        doc.fileExtension ?? doc.FileExtension ?? "",
      )
        .trim()
        .replace(/^\./, "");
      const fallbackTitle = String(doc.title ?? "document").trim() || "document";
      const name =
        doc.name ??
        (extension ? `${fallbackTitle}.${extension}` : fallbackTitle);
      const fileType =
        doc.fileType ??
        (extension
          ? `application/${extension.toLowerCase()}`
          : "application/octet-stream");
      dispatch({
        type: ON_FILEUPLOAD_CHANGE,
        payload: [{ name, type: fileType }],
      });
    }
  }, [documentFromList, selectedDocRevision, dispatch]);

  // Propagate class and format from API-fetched revision info to main check-in state
  // so they are available when the user switches to the New Document tab.
  useEffect(() => {
    if (selectedRevisionItemFromSlice?.class) {
      onDocClassFormatChange(
        selectedRevisionItemFromSlice.class,
        selectedRevisionItemFromSlice.format || "",
        "N",
        "N",
        "N",
        null,
        0
      );
    }
  }, [selectedRevisionItemFromSlice, onDocClassFormatChange]);

  const displayText = {
    objectConnection: "Object Connection(s)",
    next: "Next",
    cancel: "Cancel",
    checkin: "Check-in",
    checkinNew: "Check-in New",
    checkinNewRev: "Check-in New Revision",
    selectedObjects: "Selected Object Connection(s)",
    recent: "Recently Used",
    searchObjects: "Search Object Connection(s)",
  };

  const theme = useTheme();
  const styleTheme = useCommonControlsStyle();
  const panelStyle = useCheckinPanelStyle();

  const navigate = useNavigate();
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const [isCurrentFileTypeValid, setIsCurrentFileTypeValid] = useState(true);
  const [newRevisionWarMsg, setNewRevisionWarMsg] = useState("");
  const [showFieldValidationErrors, setShowFieldValidationErrors] =
    useState(true);

  const handleReleaseChange = (event) => {
    setLocalReleaseChecked(!!event?.target?.checked);
  };

  const handleDeleteOneDriveChange = (event) => {
    setLocalDeleteOneDrive(!!event?.target?.checked);
  };

  const docReleaseStatusForSubmit = localReleaseChecked ? "Released" : (docReleaseStatus ?? "");

  const isUploadDisabled = () => {
    return shouldShowAutoGenerateToggle && localCreateViewCopy === true;
  };


  // Set when the user dismisses the check-in progress popup with the close (X).
  // The check-in keeps running and finishes silently; only the post-success
  // navigation to All Documents is skipped (the success toast still shows).
  const progressDialogDismissedRef = React.useRef(false);

  const postCheckinFlowStartedRef = React.useRef(false);

  // Runs the post-check-in success flow: recent-doc info, list refreshes, the
  // success toast, navigation, and the slow OneDrive/view-copy/release/delete
  // tasks. It is called either from the completion effect (normal path), or
  // directly from the submit closure when the user dismissed the progress popup
  // — so it still runs after the page has unmounted, exactly like the
  // new-document check-in flow.
  const runNewRevisionSuccessFlow = (res, { navigateToAllDocs }) => {
    if (postCheckinFlowStartedRef.current) return;
    postCheckinFlowStartedRef.current = true;

    const needsSeparateViewCopyUpload =
      uploadViewCopyCount > 0 &&
      viewCopyFiles?.length > 0 &&
      selectedDocRevision != null;
    const recentDocInfo = {
      docClass: res?.docClass ?? res?.documentRevision?.docClass ?? selectedDocRevision?.class ?? selectedDocRevision?.docClass ?? "",
      docNo: res?.docNo ?? res?.documentRevision?.docNo ?? selectedDocRevision?.docNo ?? "",
      docRev: res?.docRev ?? res?.documentRevision?.docRev ?? localNewRevision ?? selectedDocRevision?.docRev ?? "",
      docSheet: res?.docSheet ?? res?.documentRevision?.docSheet ?? selectedDocRevision?.sheetNo ?? selectedDocRevision?.docSheet ?? "",
      docFormat: res?.docFormat ?? res?.documentRevision?.docFormat ?? selectedDocRevision?.format ?? "",
      title: res?.title ?? res?.documentRevision?.title ?? selectedDocRevision?.title ?? selectedDocRevision?.Title ?? "",
    };
    dispatch({
      type: ON_ATTACHED_DOC_INFO,
      payload: recentDocInfo,
    });

    dispatch(requestRefreshCheckInNewList());
    onRequestRefreshAllDocuments?.();
    consumeNavigateToAllDocuments();
    resetCheckinNewRevision();
    clearNewRevisionInfo();
    // Normal path lands the user on All Documents. On the dismissed path the
    // page has already navigated to the originating list (working documents /
    // channel documents), so navigation is skipped here.
    if (navigateToAllDocs) {
      navigate(APP_ROUTES.allDocuments);
    }

    const successDocumentTitle =
      selectedDocRevision?.title ||
      documentFromList?.title ||
      documentFromList?.name ||
      "Document";
    setTimeout(() => {
      onSuccessMessageInvoke({
        type: SUCCESS_TYPE,
        title: "New revision created",
        message: `${successDocumentTitle} checked in successfully!`,
      });
    }, 0);

    const tryApproveAndRelease = async (docInfo) => {
      if (docReleaseStatusForSubmit !== "Released") return;
      const docClass = docInfo?.docClass ?? "";
      const docNo = docInfo?.docNo ?? "";
      const docSheet = docInfo?.docSheet ?? "";
      const docRev = docInfo?.docRev ?? "";
      if (!docClass || !docNo || !docSheet || !docRev) return;

      logger.info("checkinNewRevision approving and releasing revision", {
        docNo,
        docSheet,
        docRev,
      });
      try {
        await approveAndReleaseRevision({
          docClass,
          docNo,
          docSheet,
          docRev,
        });
        logger.info("checkinNewRevision release completed");
      } catch (releaseErr) {
        logger.info("checkinNewRevision release error", releaseErr);
      }
    };

    // OneDrive metadata, view-copy upload, release, and deletion can be slow — run after navigation.
    const runPostCheckinTasks = async () => {
      if (
        documentFromList?.key &&
        documentFromList.source !== "browse" &&
        !localDeleteOneDrive
      ) {
        // Keep SharePoint identifiers on the leftover OneDrive file (filename revision, e.g. A24).
        // Only FileState is updated to CheckedIn. Rewriting DocRev to the new IFS revision (A35)
        // made this row pick up A35's check-out status while the list still labeled it A24.
        const fromName = parseIfsIdentifiersFromFileName(documentFromList.name ?? "");
        const originalDocNo = String(fromName.docNo ?? "").trim().toUpperCase();
        const targetDocNo = String(recentDocInfo.docNo ?? "").trim().toUpperCase();
        const isCrossDocumentCheckin =
          originalDocNo !== "" && targetDocNo !== "" && originalDocNo !== targetDocNo;

        if (!isCrossDocumentCheckin) {
          const { groupId: metaGroupId, fileRoot: metaFileRoot } = resolveGroupId(teamsContext);
          const metaTenantId = teamsContext?.user?.tenant?.id || tenantId || "";
          const metaWebUser = teamsContext?.user?.userPrincipalName || teamsContext?.user?.displayName || "";
          try {
            const userAuth = new UserAuth();
            const tokenResponse = await userAuth.getUserToken(true);
            const accessToken = tokenResponse?.accessToken ?? null;
            if (accessToken) {
              await updateOneDriveMetadata(accessToken, {
                userId: teamsContext?.user?.id ?? "",
                tenantId: metaTenantId,
                teamId: metaGroupId || "",
                channelId: teamsContext?.channel?.id || "",
                oneDriveRoot: metaFileRoot || "",
                documentId: String(documentFromList.key),
                webUser: metaWebUser,
                metadata: {
                  docClass: fromName.docClass || recentDocInfo.docClass,
                  docNo: fromName.docNo || recentDocInfo.docNo,
                  docRev: fromName.docRev || recentDocInfo.docRev,
                  docSheet: fromName.docSheet || recentDocInfo.docSheet,
                  formatSize: recentDocInfo.docFormat ?? selectedDocRevision?.format ?? "",
                  state: docReleaseStatusForSubmit || "",
                  fileState: "CheckedIn",
                  owner: metaWebUser,
                  lastActionDate: new Date().toISOString(),
                  // Keep download flag so Working Documents icon stays download,
                  // not checkout, when IFS FileState is still Checked Out.
                  isDownloaded: isWorkingDocumentDownloaded(documentFromList),
                },
              });
              logger.info("checkinNewRevision updated OneDrive metadata after check-in");
            }
          } catch (metaErr) {
            logger.info("checkinNewRevision metadata update error", metaErr);
          }
        } else {
          logger.info(
            "checkinNewRevision skipping metadata update: cross-document check-in, original OneDrive file retained",
            { originalDocNo, targetDocNo },
          );
        }
      }

      if (needsSeparateViewCopyUpload) {
        const docNo = res?.docNo ?? res?.documentRevision?.docNo ?? selectedDocRevision.docNo ?? "";
        const docSheet = res?.docSheet ?? res?.documentRevision?.docSheet ?? selectedDocRevision.sheetNo ?? selectedDocRevision.docSheet ?? "";
        const docRev = res?.docRev ?? res?.documentRevision?.docRev ?? localNewRevision ?? selectedDocRevision.docRev ?? "";
        const docTitle = res?.title ?? res?.documentRevision?.title ?? selectedDocRevision.title ?? selectedDocRevision.Title ?? "";
        const docClass = res?.docClass ?? res?.documentRevision?.docClass ?? selectedDocRevision.class ?? selectedDocRevision.docClass ?? "";
        const viewCopyFile = viewCopyFiles[0];
        logger.info("checkinNewRevision uploading view copy to new revision", {
          docNo,
          docSheet,
          docRev,
          viewCopyCount: viewCopyFiles?.length,
        });
        try {
          const userAuth = new UserAuth();
          const tokenResponse = await userAuth.getUserToken(true);
          const accessToken = tokenResponse?.accessToken ?? null;
          if (!accessToken) {
            throw new Error("No access token");
          }

          const base64Length = viewCopyFile?.base64str?.length ?? 0;
          const viewCopyTimeoutMs = Math.max(120000, Math.ceil(base64Length / 1024) * 50);

          await postCheckinDocument(
            accessToken,
            {
              checkinViewCopy: true,
              docClass,
              docNo,
              docSheet,
              docRev,
              title: `${docTitle} View Copy`,
              fileData: viewCopyFile.base64str,
              fileName: sanitizeFileName(viewCopyFile.name),
              rollbackRevisionOnFail: isViewCopyRequired,
              createViewCopy: false,
              webUser: new AuthTeams().getEmail(),
              status: "",
            },
            { timeout: viewCopyTimeoutMs },
          );
          logger.info("checkinNewRevision view copy upload completed");
        } catch (e) {
          logger.info("checkinNewRevision view copy upload error", e);
        }
      }

      await tryApproveAndRelease(recentDocInfo);

      if (
        !needsSeparateViewCopyUpload &&
        localDeleteOneDrive &&
        documentFromList?.key &&
        documentFromList.source !== "browse"
      ) {
        const { groupId: deleteGroupId, fileRoot: deleteFileRoot } = resolveGroupId(teamsContext);
        const effectiveDeleteGroupId = deleteGroupId || teamsContext?.team?.groupId || "";
        const deleteMembershipType = teamsContext?.channel?.membershipType || "Regular";
        const deleteChannelId = teamsContext?.channel?.id || "";

        try {
          await deleteOneDriveDocument(
            deleteMembershipType,
            documentFromList.key,
            effectiveDeleteGroupId,
            null,
            DOC_CHECK_IN_TYPE.checkinNewRevision,
            deleteFileRoot,
            deleteChannelId,
          );
        } catch (_e) {
          logger.info("checkinNewRevision deletion error", _e);
        }

        setTimeout(() => {
          refreshWorkingDocuments(50).catch(() => {});
        }, 0);
      }
    };
    runPostCheckinTasks();
  };

  // Normal (non-dismissed) completion path: when the check-in succeeds while the
  // page is still mounted, run the success flow and land on All Documents. When
  // the user dismissed the popup, the submit closure drives the flow instead
  // (so it survives the page unmounting), so this effect skips it.
  useEffect(() => {
    if (!navigateToAllDocuments) {
      postCheckinFlowStartedRef.current = false;
      return;
    }
    if (checkinSubmitLoading) return;
    if (!checkinSubmitData || checkinSubmitError) return;
    if (progressDialogDismissedRef.current) return;
    runNewRevisionSuccessFlow(checkinSubmitData, { navigateToAllDocs: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navigateToAllDocuments,
    checkinSubmitLoading,
    checkinSubmitData,
    checkinSubmitError,
  ]);

  const isOriginalDocumentWord = useMemo(() => {
    const doc = documentFromList;
    if (!doc) return false;

    const extFromDoc =
      doc.fileExtension ?? doc.FileExtension ?? "";
    const normalizedExt = String(extFromDoc).trim().toUpperCase();
    if (["DOCX", "DOC", "DOCM", "DOTM"].includes(normalizedExt)) return true;

    const name = doc.name ?? doc.title ?? uploadFiles?.[0]?.name ?? "";
    if (typeof name === "string" && name.includes(".")) {
      const ext = String(name.split(".").pop() ?? "").trim().toUpperCase();
      if (["DOCX", "DOC", "DOCM", "DOTM"].includes(ext)) return true;
    }

    // Fallback: check MIME-type-ish values if present.
    const fileType = doc.fileType ?? "";
    if (typeof fileType === "string") {
      const ft = fileType.toLowerCase();
      return (
        ft.includes("wordprocessingml") ||
        ft.includes("msword") ||
        ft.includes("application/vnd.ms-word") ||
        ft.includes("application/doc")
      );
    }

    return false;
  }, [documentFromList, uploadFiles?.[0]?.name]);

  function getViewCopyToolTip() {
    return !documentFromList || isOriginalDocumentWord ? "" : CREATE_VIEW_COPY_SUPPORT;
  }

  const shouldShowAutoGenerateToggle = isOriginalDocumentWord;

  useEffect(() => {
    if (!shouldShowAutoGenerateToggle && localCreateViewCopy) {
      setLocalCreateViewCopy(false);
    }
  }, [shouldShowAutoGenerateToggle, localCreateViewCopy]);


  useEffect(() => {
    if (isViewCopyRequired) {
      setIsAccordionExpanded(true);
    } else {
      setIsAccordionExpanded(false);
    }
  }, [isViewCopyRequired]);

  useEffect(() => {
    const first = viewCopyFiles?.[0];
    setLocalViewCopyFileType(first?.name ? (first.name.split(".").pop() ?? "") : "");
  }, [viewCopyFiles]);


  const handleAccordionChange = (event, isExpanded) => {
    setIsAccordionExpanded(isExpanded);
  };

  const navigateToCheckinExit = () => {
    const fromWorkingDocuments =
      location.state?.from === APP_ROUTES.workingDocuments;
    const fromWorkingDocumentCheckInHub =
      location.pathname === APP_ROUTES.workingDocumentCheckIn;
    const fromAllDocuments = location.state?.from === APP_ROUTES.allDocuments;

    const isInCheckInForm =
      location.pathname === APP_ROUTES.checkInNewForm ||
      location.pathname === APP_ROUTES.checkInNewRevision ||
      location.pathname === APP_ROUTES.workingDocumentCheckIn ||
      location.pathname === APP_ROUTES.checkIn;

    if (
      fromWorkingDocuments ||
      fromWorkingDocumentCheckInHub ||
      activePage === PAGE_NAME.home
    ) {
      onSelectedPageChange(PAGE_NAME.home);
      navigate(APP_ROUTES.workingDocuments, {
        state: { [NAV_LOCATION_STATE.skipWorkingDocumentsRefresh]: true },
      });
    } else if (fromAllDocuments) {
      navigate(APP_ROUTES.allDocuments);
    } else if (isInCheckInForm) {
      navigate(APP_ROUTES.checkInNew);
    } else {
      navigate(APP_ROUTES.checkInNew);
    }
  };

  function onCancel() {
    onClear();
  }

  function onClear() {
    setSelectedDocRevision(null);
    setDocumentFromList(null);
    setLocalViewCopyFileType("");
    closeCheckinDialog();
    resetCheckinNewRevision();
    clearNewRevisionInfo();
    setNewRevisionWarMsg("");
    navigateToCheckinExit();
  }

  function isButtonDisabled() {
    const hasDocumentFromList = documentFromList !== null;
    const hasSelectedRevision = revisionItemForForm != null;
    const hasNewRevision =
      localNewRevision !== undefined &&
      localNewRevision !== null &&
      localNewRevision !== "" &&
      localNewRevisionText !== undefined &&
      localNewRevisionText !== null &&
      localNewRevisionText !== "";
    let enabled = hasDocumentFromList && hasSelectedRevision && hasNewRevision;
    const hasViewCopySatisfied =
      uploadViewCopyCount > 0 || localCreateViewCopy === true;
    if (enabled && isViewCopyRequired === true && !hasViewCopySatisfied) {
      enabled = false;
    }
    if (!enabled) {
      logger.debug("Check-in button disabled – missing", {
        documentFromList: hasDocumentFromList,
        hasSelectedRevision,
        localNewRevision: hasNewRevision,
        viewCopyRequired: isViewCopyRequired,
        hasViewCopySatisfied,
      });
    }
    return !enabled;
  }

  const handleChange = (event) => {
    const result = event.target.value.toUpperCase();
    if (result.length >= 6) {
      setNewRevisionWarMsg(CHECKIN_VALIDATION_NEW_REVISION_MAX_LENGTH);
    } else {
      setNewRevisionWarMsg("");
    }
    setLocalNewRevision(result);
    if (!hasUserEditedRevisionTextRef.current) {
      setLocalNewRevisionText(result);
    }
  };

  const cancelButton = (
    <div style={{ marginRight: "10px" }}>
      <PrimaryButton
        styles={{
          root: {
            marginRight: "10px",
            fontFamily: theme.fonts.regular,
            fontSize: `${theme.fontSizes?.normal ?? 12}px`,
          },
        }}
        style={styleTheme.CancelButtonFillStyle}
        id="btncancel"
        text={displayText.cancel}
        onClick={(ev) => {
          onCancel();
        }}
      />
    </div>
  );

  /**
   * @param {Blob} file
   * @returns {Promise<string>}
   */
  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
        reader.onload = () => {
          const result = typeof reader.result === "string" ? reader.result : "";
          const commaIndex = result.indexOf(",");
          resolve(commaIndex >= 0 ? result.substring(commaIndex + 1) : result);
        };
        reader.readAsDataURL(file);
      } catch (err) {
        reject(err);
      }
    });

  /**
   * @param {string} documentDownloadUrl - URL used to fetch file content (must be a download URL; Doc.aspx webUrl is CORS-blocked).
   */
  const checkinDocument = async (
    documentDownloadUrl,
    selectedRevisionItem,
    newRevision,
    newRevisionText,
    uploadFiles,
    createViewCopy,
    newRevisionObjCon,
    uploadViewCopyCount,
    viewRequired,
    docReleaseStatus,
    formatRequired,
  ) => {
    progressDialogDismissedRef.current = false;
    logger.info("checkinDocument started", {
      documentDownloadUrlLength: documentDownloadUrl?.length,
      selectedRevisionItem: selectedRevisionItem
        ? { class: selectedRevisionItem.class, docNo: selectedRevisionItem.docNo, docRev: selectedRevisionItem.docRev }
        : null,
      newRevision,
      createViewCopy,
      uploadViewCopyCount,
      viewRequired,
      uploadFilesCount: uploadFiles?.length ?? 0,
    });

    const className = selectedRevisionItem?.class ?? "";
    let isValid = true;
    if (viewRequired === true) {
      if (uploadViewCopyCount > 0 || createViewCopy === true) {
        onValidationError(null);
      } else {
        logger.info("checkinDocument view copy validation failed", { className });
        isValid = false;
      }
    } else {
      onValidationError(null);
    }
    if (isValid) {
      const checkinFile = uploadFiles?.[0];
      if (!checkinFile) {
        logger.info("checkinDocument no file selected");
        closeCheckinDialog();
        onValidationError("No file selected for check-in. Please upload a document.");
        return;
      }
      logger.info("checkinDocument submitting check-in new revision (server downloads from fileUrl)", {
        documentDownloadUrlLength: documentDownloadUrl?.length ?? 0,
      });
      const allFiles = [
        {
          name: checkinFile.name,
          type: checkinFile.type,
          ...(checkinFile.base64str ? { base64str: checkinFile.base64str } : {}),
        },
      ];
      const submitAction = await submitCheckinNewRevision({
        selectedRevisionItem,
        newRevision,
        newRevisionText,
        uploadFiles: allFiles,
        fileUrl: checkinFile.base64str ? undefined : documentDownloadUrl,
        createViewCopy,
        newRevisionObjCon,
        uploadViewCopyCount,
        viewRequired,
        docReleaseStatus,
        viewCopyFiles: uploadViewCopyCount > 0 && viewCopyFiles?.length > 0 ? viewCopyFiles : undefined,
        formatRequired,
        __toast: { suppressToast: true },
      });
      logger.info("checkinDocument submitCheckinNewRevision completed");

      // If the user dismissed the progress popup, the page has already navigated
      // to the originating list and the completion effect won't run. Drive the
      // success flow here (toast + refreshes + OneDrive/view-copy/release/delete)
      // so it still happens after unmount, just like the new-document flow.
      if (progressDialogDismissedRef.current) {
        const res = submitAction?.payload?.data ?? null;
        if (res && !submitAction?.error) {
          runNewRevisionSuccessFlow(res, { navigateToAllDocs: false });
        }
      }
    }
  };

  // Close (X) on the check-in progress popup: navigate the user back to the
  // originating list (working documents / channel documents) right away. The
  // background check-in keeps running and finishes silently — on success only
  // the toast shows (no navigation to All Documents), driven from the submit
  // closure above so it survives this page unmounting.
  const handleDismissProgressPopup = () => {
    progressDialogDismissedRef.current = true;
    closeCheckinDialog();
    navigateToCheckinExit();
  };

  const handleCloseProcessDialog = () => {
    closeCheckinDialog();
    if (checkinSubmitError) {
      // Let the user pick a fresh document after an error without auto-reloading the stale prefill.
      setSuppressCurrentPrefill(true);
      hasAppliedRevisionPrefillRef.current = true;
      handleClearSelectedDocument();
      setShowFieldValidationErrors(false);
      setNewRevisionWarMsg("");
      hasUserEditedRevisionTextRef.current = false;
      setDocumentFieldFocusToken((value) => value + 1);
      return;
    }

    resetCheckinNewRevision();
    handleClearSelectedDocument();
    setDocumentFromList(null);
    setFetchedDownloadUrl("");
    dispatch(clearOneDriveDownloadUrl());
    setNewRevisionWarMsg("");
    setShowFieldValidationErrors(false);
    hasUserEditedRevisionTextRef.current = false;
    // Successful dialog close should not auto-reapply the stale location prefill into the cleared form.
    hasAppliedRevisionPrefillRef.current = true;
  };

  const checkinButton = (
    <PrimaryButton
      text={checkinSubmitLoading ? "Checking in…" : displayText.checkin}
      disabled={isButtonDisabled() || checkinSubmitLoading}
      style={
        isButtonDisabled() || checkinSubmitLoading
          ? styleTheme.DisableButtoneStyle
          : styleTheme.PrimaryButtonFillStyle
      }
      onClick={async () => {
        setShowFieldValidationErrors(true);
        logger.info("Check-in button clicked");
        const isBrowseSource = documentFromList?.source === "browse";
        let documentDownloadUrl = "";
        let filesForCheckin = [];

        if (isBrowseSource) {
          const browseFileEntry = uploadFiles?.[0] ?? null;
          const rawFile = browseFileEntry?.file ?? documentFromList?.file ?? null;
          let base64str =
            typeof browseFileEntry?.base64str === "string" &&
            browseFileEntry.base64str.trim()
              ? browseFileEntry.base64str.trim()
              : "";
          if (!base64str && rawFile) {
            try {
              base64str = await readFileAsBase64(rawFile);
            } catch (err) {
              logger.info("checkinNewRevision browse file read failed", {
                message: err?.message ?? String(err),
              });
            }
          }
          if (!base64str) {
            logger.info("checkinDocument aborted: could not read browse file bytes");
            onValidationError(
              "Could not read the selected file. Please select the document again."
            );
            return;
          }
          filesForCheckin = [
            {
              name: browseFileEntry?.name ?? documentFromList?.name ?? "document",
              type:
                browseFileEntry?.type ??
                documentFromList?.fileType ??
                "application/octet-stream",
              base64str,
            },
          ];
          logger.info("checkinDocument browse file prepared", {
            fileName: filesForCheckin[0].name,
            base64Length: base64str.length,
          });
        } else {
          // Do not use Redux OneDrive `downloadUrl` for submit — it is not keyed by document id.
          const fromState = location.state?.selectedDocumentDownloadUrl;
          documentDownloadUrl =
            fromState ??
            documentFromList?.downloadlink ??
            fetchedDownloadUrl ??
            selectedDocRevision?.webUrl ??
            documentFromList?.weblink ??
            documentFromList?.webLinkUrl;

          let resolvedViaClickFetch = false;
          // General App: same scope as list fetch — if URL still missing (e.g. slow effect or stale storage), fetch once here.
          if (!documentDownloadUrl && documentFromList?.key) {
            const { groupId: g, channelId: c, fileRoot } = resolveGroupId(teamsContext);
            const tid = teamsContext?.user?.tenant?.id || tenantId || "";
            if (tid && fileRoot) {
              try {
                documentDownloadUrl = await dispatch(
                  fetchOneDriveDownloadUrlThunk({
                    teamId: g || "",
                    channelId: c || "",
                    tenantId: tid,
                    documentId: String(documentFromList.key),
                    fileRoot: String(fileRoot),
                    operationType: DOC_CHECK_IN_TYPE.checkinNewRevision,
                  })
                ).unwrap();
                if (documentDownloadUrl) {
                  resolvedViaClickFetch = true;
                  setFetchedDownloadUrl(documentDownloadUrl);
                }
              } catch (err) {
                logger.info("checkinNewRevision onClick fetch download URL failed", {
                  message: err?.message ?? String(err),
                });
              }
            }
          }

          const urlSource = resolvedViaClickFetch
            ? "onClick fetch (resolveGroupId / General App safe)"
            : fromState
              ? "location.state.selectedDocumentDownloadUrl"
              : documentFromList?.downloadlink
                ? "documentFromList.downloadlink"
                : fetchedDownloadUrl
                  ? "fetchedDownloadUrl (per current list row)"
                  : selectedDocRevision?.webUrl
                    ? "selectedDocRevision.webUrl"
                    : documentFromList?.weblink
                      ? "documentFromList.weblink"
                      : documentFromList?.webLinkUrl
                        ? "documentFromList.webLinkUrl"
                        : "none (no URL from list, navigation state, or fetch)";
          logger.info("checkinDocument URL resolved", {
            hasDocumentDownloadUrl: !!documentDownloadUrl,
            documentDownloadUrlLength: documentDownloadUrl?.length,
            source: urlSource,
          });
          if (!documentDownloadUrl) {
            logger.info("checkinDocument aborted: no document download URL (API will not run)");
            onValidationError(
              "Could not resolve a download URL for this document. In General App, ensure you opened this screen from Check-in New or Working Documents. If the problem continues, try selecting the document again."
            );
            return;
          }
          // When coming from list, uploadFiles can be empty; use documentFromList as the file to check in (name/type), content is fetched from documentDownloadUrl
          filesForCheckin =
            uploadFiles?.length > 0
              ? uploadFiles
              : documentFromList
                ? [
                  {
                    name: documentFromList.name ?? "document",
                    type: documentFromList.fileType ?? "application/octet-stream",
                  },
                ]
                : [];
          logger.info("checkinDocument filesForCheckin", {
            fromList: uploadFiles?.length === 0 && !!documentFromList,
            count: filesForCheckin.length,
          });
        }
        await checkinDocument(
          documentDownloadUrl,
          // Use enriched revision item (includes FormatSize/docFormat when required)
          selectedRevisionItemFromSlice ?? selectedDocRevision,
          localNewRevision,
          localNewRevisionText,
          filesForCheckin,
          localCreateViewCopy,
          localNewRevisionObjCon,
          uploadViewCopyCount,
          isViewCopyRequired,
          docReleaseStatusForSubmit,
          isFormatRequired,
        );
      }}
    />
  );

  const handleConfirm = async (row) => {
    if (!row) return;
    setSelectedDocRevision(row);
    await fetchNewRevisionInfo(row, {
      // Needed to get required fields like FormatSize (docFormat) for some classes
      requestDocRevInfo: true,
      documentCheckInType: DOC_CHECK_IN_TYPE.checkinNewRevision,
      uploadFiles: uploadFiles ?? [],
      uploadViewCopyCount: uploadViewCopyCount ?? 0,
    });
  };

  const handleClearSelectedDocument = React.useCallback(() => {
    // User-initiated clear should not immediately rehydrate from location fallback.
    setSuppressCurrentPrefill(true);
    hasAppliedRevisionPrefillRef.current = true;
    setSelectedDocRevision(null);
    clearNewRevisionInfo();
    setLocalNewRevision("");
    setLocalNewRevisionText("");
    setLocalCreateViewCopy(false);
    setLocalNewRevisionObjCon([]);
    setLocalReleaseChecked(false);
    setLocalDeleteOneDrive(true);
    setLocalViewCopyFileType("");
    dispatch({
      type: ON_FILEUPLOAD_CHANGE,
      payload: [],
    });
    dispatch({
      type: ON_VIEWREQUIRED_CHANGED,
      payload: "N",
    });
  }, [clearNewRevisionInfo, dispatch]);

  const hasUploadedFiles = uploadViewCopyCount > 0;
  const leftBoxHeight = "147px";
  const rightBoxHeight = "180px";
  const uploadBoxHeight = hasUploadedFiles ? "110px" : rightBoxHeight;

  const documentError =
    showFieldValidationErrors && !revisionItemForForm
      ? CHECKIN_VALIDATION_DOCUMENT_REQUIRED
      : "";
  const newRevisionError =
    showFieldValidationErrors && !localNewRevision?.trim()
      ? CHECKIN_VALIDATION_NEW_REVISION_REQUIRED
      : "";
  const revisionTextError =
    showFieldValidationErrors && !localNewRevisionText?.trim()
      ? CHECKIN_VALIDATION_REVISION_TEXT_REQUIRED
      : "";
  const viewCopyError =
    showFieldValidationErrors &&
    isViewCopyRequired &&
    uploadViewCopyCount === 0 &&
    !localCreateViewCopy
      ? CHECKIN_VALIDATION_VIEW_COPY_REQUIRED
      : "";

  // Release requires a document/revision selected and its revision data fully loaded.
  const isReleaseDisabled =
    !revisionItemForForm || newRevisionInfoLoading === true;

  const handleChangeText = (value) => {
    const nextValue = value ?? "";
    setLocalNewRevisionText(nextValue);
    hasUserEditedRevisionTextRef.current = nextValue !== (localNewRevision ?? "");
  };

  return (
    <>
      <Stack
        enableScopedSelectors
        tokens={{ childrenGap: 0 }}
        styles={{
          root: {
            height: "100%",
            flex: "1 1 auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Stack.Item styles={panelStyle.docRevisionPanelStyle}>
          <Stack
            styles={{
              root: {
                height: "100%",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            <Stack.Item
              styles={{
                root: {
                  ...panelStyle.panelStyle.root,
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                },
              }}
            >
              <ScrollablePane
                scrollbarVisibility={ScrollbarVisibility.auto}
                styles={{
                  root: {
                    height: "100%",
                    minHeight: 0,
                    selectors: {
                      ".ms-ScrollablePane--contentContainer": {
                        scrollbarColor: `${theme.colors.scrollbarThumb} ${theme.colors.scrollbarBackground}`,
                        paddingRight: 5,
                        boxSizing: "border-box",
                      },

                      ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-track":
                      {
                        background: theme.colors.scrollbarBackground,
                      },
                      ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb":
                      {
                        background: theme.colors.scrollbarThumb,
                      },
                    },
                  },
                }}
              >
                <Stack
                  enableScopedSelectors
                  style={{ marginTop: "10px" }}
                  horizontalAlign="center"
                  verticalAlign="center"
                  tokens={{ childrenGap: 5 }}
                >
                  <RevisionDocumentFields
                    selectedRevisionItem={revisionItemForForm}
                    onSelectRevisionItem={(row) => handleConfirm(row)}
                    onClearSelection={handleClearSelectedDocument}
                    showFieldValidation={showFieldValidationErrors}
                    documentError={documentError}
                    focusToken={documentFieldFocusToken}
                  />
                  <FileTypeMismatchAlert
                    show={!isCurrentFileTypeValid}
                    message={getFileTypeMismatchError()}
                  />
                  <NewRevisionInfoContent
                    isError={newRevisionInfoError}
                    errorMessage={newRevisionInfoMessage}
                    isLoading={newRevisionInfoLoading}
                    latestRevision={latestRevisionForDisplay}
                    newRevision={localNewRevision}
                    onNewRevisionChange={handleChange}
                    newRevisionWarMsg={newRevisionWarMsg}
                    newRevisionText={localNewRevisionText}
                    onNewRevisionTextChange={handleChangeText}
                    showFieldValidation={showFieldValidationErrors}
                    newRevisionError={newRevisionError}
                    revisionTextError={revisionTextError}
                  />
                  <RevisionValidationMessageBar
                    show={
                      docCheckinType === CHECKIN_NEW_REVISION &&
                      !!newRevisionValidationError &&
                      !viewCopyError
                    }
                    message={newRevisionValidationError}
                  />
                  <ViewCopyAccordionSection
                    expanded={isAccordionExpanded}
                    onAccordionChange={handleAccordionChange}
                    showAutoGenerateToggle={shouldShowAutoGenerateToggle}
                    createViewCopy={localCreateViewCopy}
                    onCreateViewCopyChange={(check) => {
                      if (check === true && viewCopyFiles?.length > 0) {
                        // optional: clear view copy when enabling auto-generate
                      }
                      setLocalCreateViewCopy(check);
                    }}
                    viewCopyToolTip={getViewCopyToolTip()}
                    leftBoxHeight={leftBoxHeight}
                    rightBoxHeight={rightBoxHeight}
                    hasUploadedFiles={hasUploadedFiles}
                    uploadBoxHeight={uploadBoxHeight}
                    isUploadDisabled={isUploadDisabled()}
                    localViewCopyFileType={localViewCopyFileType}
                    viewCopyError={viewCopyError}
                  />

                  <Stack.Item
                    grow
                    align="center"
                    style={{ width: "100%", justifyItems: "center" }}
                  >
                    <ReleaseDocumentCheckbox
                      checked={localReleaseChecked}
                      onChange={handleReleaseChange}
                      disabled={isReleaseDisabled}
                    />
                  </Stack.Item>

                  {!!documentFromList && documentFromList.source !== "browse" && (
                    <Stack.Item
                      grow
                      align="center"
                      style={{ width: "100%", justifyItems: "center" }}
                    >
                      <DeleteOneDriveCheckbox
                        checked={localDeleteOneDrive}
                        onChange={handleDeleteOneDriveChange}
                      />
                    </Stack.Item>
                  )}

                </Stack>
              </ScrollablePane>
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item
          enableScopedSelectors
          horizontalAlign="center"
          styles={panelStyle.buttonPanelStyle}
        >
          <Stack
            enableScopedSelectors
            horizontal
            tokens={{ childrenGap: 10 }}
          >
            {cancelButton}
            {checkinButton}
          </Stack>
        </Stack.Item>

      </Stack>
      <ReusableMessageDialog
        isOpen={checkinDialogOpen}

        onClose={handleCloseProcessDialog}
        title={
          checkinSubmitError
            ? "Check-in failed"
            : checkinSubmitData
              ? "Check-in successful"
              : "Check-in New Revision"
        }
        showHeader={checkinSubmitError || !!checkinSubmitData}
        message={
          checkinSubmitError
            ? checkinSubmitMessage || "Check-in failed."
            : checkinSubmitData
              ? "Document checked in successfully."
              : "Checking in document…"
        }
        errorMessage={checkinSubmitError ? checkinSubmitMessage : ""}
        variant={
          checkinSubmitError
            ? "error"
            : checkinSubmitData
              ? "info"
              : "normal"
        }
        isLoading={checkinSubmitLoading}
        loadingText="Checking in…"
        dialogMinHeight={60}
        bodyMinHeight={50}
        showCloseIcon={
          checkinSubmitLoading && !checkinSubmitError && !checkinSubmitData
        }
        onCloseIcon={handleDismissProgressPopup}
        closeIconTooltip="Check-in will still continue after closing"
      />
    </>
  );
};

export default CheckinNewRevision;
